/**
 * Leaderboard Full Component
 * VOY-11: Complete leaderboard with prize breakdown
 */

import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Clock, Sparkles, ShoppingCart } from 'lucide-react';
import { GamePool, LeaderboardEntry, getTierEmoji } from '@/data/gaming-mockup';

interface LeaderboardFullProps {
  pool: GamePool;
  entries: LeaderboardEntry[];
  yourScore: number;
  attemptsLeft: number;
  onPlayAgain: () => void;
  onBuyBoosters: () => void;
}

export function LeaderboardFull({
  pool,
  entries,
  yourScore,
  attemptsLeft,
  onPlayAgain,
  onBuyBoosters
}: LeaderboardFullProps) {
  const yourEntryRef = useRef<HTMLDivElement>(null);
  const tierEmoji = getTierEmoji(pool.tier);
  const yourEntry = entries.find(e => e.isYou);

  // Scroll to your position on mount
  useEffect(() => {
    if (yourEntryRef.current) {
      yourEntryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="rounded-3xl bg-gradient-to-r from-[#5B6FED] to-[#8B9FED] text-white border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="w-6 h-6" />
                <h2 className="text-2xl font-bold">Leaderboard</h2>
              </div>
              <p className="text-white/90">{tierEmoji} {pool.tier.charAt(0).toUpperCase() + pool.tier.slice(1)} Pool</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span className="text-xl font-bold">{pool.timeRemaining}</span>
              </div>
              <p className="text-xs text-white/80">remaining</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prize Breakdown */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-500" />
          Prize Breakdown
        </h3>
        <div className="space-y-2">
          {pool.prizes.map((prize, index) => (
            <Card key={index} className="bg-white/80 backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {index === 0 && <span className="text-2xl">🥇</span>}
                    {index === 1 && <span className="text-2xl">🥈</span>}
                    {index === 2 && <span className="text-2xl">🥉</span>}
                    <div>
                      <p className="font-bold text-gray-900">{prize.rank} Place</p>
                      <p className="text-sm text-gray-600">{prize.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-[#5B6FED]">${prize.value.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{prize.count} winner{prize.count > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Leaderboard List */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Rankings</h3>
        <Card>
          <CardContent className="p-0">
            <div className="max-h-96 overflow-y-auto">
              {entries.map((entry) => (
                <div
                  key={entry.rank}
                  ref={entry.isYou ? yourEntryRef : null}
                  className={`flex items-center justify-between p-4 border-b border-gray-100 last:border-0 ${
                    entry.isYou ? 'bg-purple-50 border-l-4 border-l-[#5B6FED]' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-bold text-lg w-12 ${
                      entry.rank === 1 ? 'text-yellow-500 text-2xl' :
                      entry.rank === 2 ? 'text-gray-400 text-xl' :
                      entry.rank === 3 ? 'text-amber-700 text-xl' :
                      'text-gray-400'
                    }`}>
                      {entry.rank === 1 ? '🥇' : 
                       entry.rank === 2 ? '🥈' : 
                       entry.rank === 3 ? '🥉' : 
                       `#${entry.rank}`}
                    </span>
                    <div>
                      <p className={`font-medium ${entry.isYou ? 'text-[#5B6FED] font-bold' : ''}`}>
                        {entry.username}
                        {entry.isYou && <span className="ml-2 text-xs bg-[#5B6FED] text-white px-2 py-0.5 rounded-full">YOU</span>}
                      </p>
                      {entry.prize && (
                        <p className="text-xs text-green-600 font-semibold">{entry.prize}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{entry.score.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{entry.attempts} attempt{entry.attempts > 1 ? 's' : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Your Stats */}
      {yourEntry && (
        <Card className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border-[#5B6FED] dark:border-blue-500 border-2 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Your Position</p>
                <p className="text-3xl font-bold text-[#5B6FED]">#{yourEntry.rank}</p>
                <p className="text-sm text-gray-600 mt-1">Score: {yourScore.toLocaleString()} pts</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Attempts Left</p>
                <p className="text-3xl font-bold text-gray-900">{attemptsLeft}/3</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        {attemptsLeft > 0 && (
          <Button
            onClick={onPlayAgain}
            className="flex-1 rounded-full bg-[#5B6FED] hover:bg-[#4A5FDD] text-white font-semibold py-6 text-lg"
          >
            🎮 Play Again
          </Button>
        )}
        <Button
          onClick={onBuyBoosters}
          variant="outline"
          className="flex-1 rounded-full border-[#5B6FED] text-[#5B6FED] hover:bg-[#E8E8FF] font-semibold py-6"
        >
          <ShoppingCart className="w-5 h-5 mr-2" />
          Buy Boosters
        </Button>
      </div>
    </div>
  );
}

